import {useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  CAPSULE_ALL_SUBJECTS,
  CAPSULE_CATEGORIES,
  CAPSULE_GRADES,
  gradeStage,
} from '#/lib/curriculum'
import {type DocumentAudience} from '#/lib/document/types'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {PaperPlane_Stroke2_Corner0_Rounded as PaperPlane} from '#/components/icons/PaperPlane'
import {
  OptionCard,
  SelectedTag,
  Step,
  StepTitle,
  SubjectOptions,
} from '#/components/publish/PublishSteps'
import {Text} from '#/components/Typography'

/**
 * Asks who a document is for, before the post that carries it goes out.
 *
 * The vocabulary is the platform's own (`#/lib/curriculum`) rather than a set
 * of PDF-specific options, so a PDF lands in the same shelves a capsule or a
 * FlashBook does: the *audience* is the category the author picks, the level
 * and subject are the codes that get stored, exactly as they are for a book.
 *
 * It asks *the same way* the capsule's publisher asks, too — the same four
 * steps in the same order, from `#/components/publish/PublishSteps` — because
 * the two are the same question about the same vocabulary, and an author who
 * has published one of them should already know how to publish the other. This
 * dialog used to ask it as a flat form under a title, with the publish press
 * under the form disabled until the last answer landed; side by side with the
 * capsule's the difference read as two different features, which is the kind of
 * drift a shared step is the cure for.
 *
 * It asks rather than publishes — the composer owns the post, so this reports
 * the choice and gets out of the way. The question is asked in three
 * progressive steps rather than on one long form because each answer decides
 * what the next list can even contain: a subject list that ignores the grade
 * is a list of wrong answers.
 */
export function PublishDocumentDialog({
  control,
  initialAudience,
  onSubmit,
}: {
  control: Dialog.DialogControlProps
  /** Pre-selects the author's last answer in this composer, so a re-post is one tap. */
  initialAudience?: DocumentAudience
  onSubmit: (audience: DocumentAudience) => void
}) {
  return (
    <Dialog.Outer control={control} testID="publishDocumentDialog">
      <Dialog.Handle />
      <Inner
        control={control}
        initialAudience={initialAudience}
        onSubmit={onSubmit}
      />
    </Dialog.Outer>
  )
}

function Inner({
  control,
  initialAudience,
  onSubmit,
}: {
  control: Dialog.DialogControlProps
  initialAudience?: DocumentAudience
  onSubmit: (audience: DocumentAudience) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  // The stored codes are the answer, but the *question* is asked by category,
  // so the category is recovered from the grade it implies.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(() =>
    gradeStage(initialAudience?.level),
  )
  const [selectedGrade, setSelectedGrade] = useState<string | null>(
    initialAudience?.level ?? null,
  )
  const [selectedSubject, setSelectedSubject] = useState<string | null>(
    initialAudience?.subject ?? null,
  )

  /*
   * The step the author is on, 1-based, counted off the answers themselves —
   * the same arithmetic the capsule's publisher uses, and the reason the two
   * cannot disagree about what is answered: there is no second record of it to
   * fall out of step. A preselected audience opens this dialog on step 4.
   */
  const activeStep = !selectedCategory
    ? 1
    : !selectedGrade
      ? 2
      : !selectedSubject
        ? 3
        : 4

  const categoryLabel = CAPSULE_CATEGORIES.find(
    c => c.value === selectedCategory,
  )?.label
  const gradeLabel = (
    selectedCategory ? CAPSULE_GRADES[selectedCategory] : []
  ).find(g => g.value === selectedGrade)?.label
  const subjectLabel = CAPSULE_ALL_SUBJECTS.find(
    s => s.value === selectedSubject,
  )?.label

  return (
    <Dialog.ScrollableInner
      testID="publishDocumentDialogInner"
      label={l`Publish Document`}
      style={[a.w_full, {maxWidth: 500}]}>
      <View style={[a.gap_2xl, a.pt_md]}>
        {/* Step 1 – Who Are You Targeting? */}
        <Step>
          <StepTitle
            index={1}
            title={l`Who Are You Targeting?`}
            activeIndex1={activeStep}
          />
          {selectedCategory ? (
            <SelectedTag
              label={l(categoryLabel ?? msg`Unknown`)}
              onClear={() => {
                setSelectedCategory(null)
                setSelectedGrade(null)
                setSelectedSubject(null)
              }}
            />
          ) : (
            <View style={[a.gap_sm]}>
              {CAPSULE_CATEGORIES.map(c => (
                <OptionCard
                  key={c.value}
                  testID={`audienceCategory-${c.value}`}
                  label={l(c.label)}
                  onSelect={() => setSelectedCategory(c.value)}
                />
              ))}
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.w_full,
                  a.px_md,
                  a.py_sm,
                  a.rounded_sm,
                  a.border,
                  [t.atoms.border_contrast_low],
                ]}>
                <Text style={[a.flex_1, a.italic, a.leading_snug]}>
                  <Trans>
                    Choose the age range your content is intended for, even if
                    it’s not a school subject. Example: Robotics for ages 12–15
                    → select College.
                  </Trans>
                </Text>
              </View>
            </View>
          )}
        </Step>

        {/* Step 2 – Select the Grade Level */}
        <Step>
          <StepTitle
            index={2}
            title={l`Select the Grade Level`}
            activeIndex1={activeStep}
          />
          {activeStep >= 2 &&
            (selectedGrade ? (
              <SelectedTag
                label={l(gradeLabel ?? msg`Unknown`)}
                onClear={() => {
                  setSelectedGrade(null)
                  setSelectedSubject(null)
                }}
              />
            ) : (
              <View style={[a.gap_sm]}>
                {(selectedCategory ? CAPSULE_GRADES[selectedCategory] : []).map(
                  g => (
                    <OptionCard
                      key={g.value}
                      testID={`audienceGrade-${g.value}`}
                      label={l(g.label)}
                      onSelect={() => setSelectedGrade(g.value)}
                    />
                  ),
                )}
              </View>
            ))}
        </Step>

        {/* Step 3 – Select the Subject or Topic */}
        <Step>
          <StepTitle
            index={3}
            title={l`Select the Subject or Topic`}
            activeIndex1={activeStep}
          />
          {activeStep >= 3 &&
            (selectedSubject ? (
              <SelectedTag
                label={l(subjectLabel ?? msg`Unknown`)}
                onClear={() => setSelectedSubject(null)}
              />
            ) : (
              <SubjectOptions
                grade={selectedGrade}
                testID={value => `audienceSubject-${value}`}
                onSelect={setSelectedSubject}
              />
            ))}
        </Step>

        {/* Step 4 – Publish Your Document, which is the press rather than a question */}
        <Step>
          <StepTitle
            index={4}
            title={l`Publish Your Document`}
            activeIndex1={activeStep}
          />
          {activeStep === 4 && (
            <View style={[a.gap_lg]}>
              {/* Says what the choice is *for*, which the pickers cannot: the
                  codes decide who the document gets shown to. */}
              <Text style={[a.text_md, a.leading_snug]}>
                <Trans>
                  Naming the audience is what lets this document reach the
                  people it was written for, and keeps it out of the feeds of
                  everyone else.
                </Trans>
              </Text>
              <Button
                testID="publishDocumentBtn"
                label={l`Publish Document`}
                size="large"
                variant="solid"
                color="primary"
                onPress={() => {
                  if (!selectedGrade || !selectedSubject) return
                  onSubmit({level: selectedGrade, subject: selectedSubject})
                  control.close()
                }}>
                <ButtonText>
                  <Trans>Publish Document</Trans>
                </ButtonText>
                <ButtonIcon icon={PaperPlane} />
              </Button>
            </View>
          )}
        </Step>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
